import {
  BusinessBusiness,
  PopularityLevel,
  PopularitySignal,
  PopularityEvidenceItem,
  PopularityResult,
  AudienceWarning,
  BusinessIdentity,
  GrowthEvidence,
  NearbyAnchor,
  LocationContext,
  PopularityDataCollection,
  PopularityIntelligence,
  PopularityAssessment,
  ExplorerPopularityLevel,
  MarketContext,
  BrandIdentity,
  NearbyPopularAnchor,
  JobCompetitionEvidence,
  PopularityEvidence
} from "../types";

export type { PopularityResult, AudienceWarning, PopularityIntelligence, ExplorerPopularityLevel, MarketContext, BrandIdentity };

// Algorithm Version Identifier for Cache Invalidation & Auditing
export const POPULARITY_ALGORITHM_VERSION = "popularity_v4";

// Standard Category Models for Adaptive Weighting
export type PopularityBusinessModel =
  | "high_frequency_consumer"
  | "membership_consumer"
  | "appointment_consumer"
  | "emergency_service"
  | "destination_business"
  | "low_frequency_consumer"
  | "b2b"
  | "online_first"
  | "unknown";

export const EXPLORER_POPULARITY_LABELS: Record<ExplorerPopularityLevel, string> = {
  extremely_popular: "Extremely Popular",
  highly_popular: "Highly Popular",
  moderately_popular: "Moderately Popular",
  limited_popularity: "Limited Popularity",
  minimal_popularity: "Minimal Popularity",
};

// Comprehensive Known Brand Registry with Entity & Chain Metadata
export interface BrandRegistryEntry {
  canonicalName: string;
  parentBrandName: string;
  chainStatus: "international" | "national" | "regional" | "local_chain" | "national_chain" | "international_chain" | "regional_chain";
  verifiedLocationCount: number;
  businessModel: PopularityBusinessModel;
  officialWebsite: string;
  aliases: string[];
}

export const BRAND_REGISTRY: BrandRegistryEntry[] = [
  {
    canonicalName: "Chick-fil-A",
    parentBrandName: "Chick-fil-A, Inc.",
    chainStatus: "national",
    verifiedLocationCount: 3000,
    businessModel: "high_frequency_consumer",
    officialWebsite: "chick-fil-a.com",
    aliases: ["chick fil a", "chickfil-a", "chick-fil-a restaurant", "chickfila"]
  },
  {
    canonicalName: "Planet Fitness",
    parentBrandName: "Planet Fitness, Inc.",
    chainStatus: "national",
    verifiedLocationCount: 2400,
    businessModel: "membership_consumer",
    officialWebsite: "planetfitness.com",
    aliases: ["planet fitness gym", "planet fitness club", "pf fitness"]
  },
  {
    canonicalName: "McDonald's",
    parentBrandName: "McDonald's Corporation",
    chainStatus: "international",
    verifiedLocationCount: 40000,
    businessModel: "high_frequency_consumer",
    officialWebsite: "mcdonalds.com",
    aliases: ["mcdonalds", "mcdonald's restaurant", "mc donalds"]
  },
  {
    canonicalName: "Starbucks",
    parentBrandName: "Starbucks Corporation",
    chainStatus: "international",
    verifiedLocationCount: 35000,
    businessModel: "high_frequency_consumer",
    officialWebsite: "starbucks.com",
    aliases: ["starbucks coffee", "starbucks cafe"]
  },
  {
    canonicalName: "Subway",
    parentBrandName: "Subway IP LLC",
    chainStatus: "international",
    verifiedLocationCount: 37000,
    businessModel: "high_frequency_consumer",
    officialWebsite: "subway.com",
    aliases: ["subway sandwiches"]
  },
  {
    canonicalName: "Walmart",
    parentBrandName: "Walmart Inc.",
    chainStatus: "international",
    verifiedLocationCount: 10500,
    businessModel: "high_frequency_consumer",
    officialWebsite: "walmart.com",
    aliases: ["walmart supercenter", "walmart neighborhood market"]
  },
  {
    canonicalName: "Target",
    parentBrandName: "Target Corporation",
    chainStatus: "national",
    verifiedLocationCount: 1950,
    businessModel: "high_frequency_consumer",
    officialWebsite: "target.com",
    aliases: ["target store"]
  },
  {
    canonicalName: "Costco",
    parentBrandName: "Costco Wholesale Corporation",
    chainStatus: "international",
    verifiedLocationCount: 850,
    businessModel: "membership_consumer",
    officialWebsite: "costco.com",
    aliases: ["costco wholesale"]
  },
  {
    canonicalName: "Home Depot",
    parentBrandName: "The Home Depot, Inc.",
    chainStatus: "national",
    verifiedLocationCount: 2300,
    businessModel: "high_frequency_consumer",
    officialWebsite: "homedepot.com",
    aliases: ["the home depot", "home depot store"]
  },
  {
    canonicalName: "Lowe's",
    parentBrandName: "Lowe's Companies, Inc.",
    chainStatus: "national",
    verifiedLocationCount: 1700,
    businessModel: "high_frequency_consumer",
    officialWebsite: "lowes.com",
    aliases: ["lowes", "lowe's home improvement"]
  },
  {
    canonicalName: "Dunkin'",
    parentBrandName: "Inspire Brands, Inc.",
    chainStatus: "national",
    verifiedLocationCount: 13200,
    businessModel: "high_frequency_consumer",
    officialWebsite: "dunkindonuts.com",
    aliases: ["dunkin", "dunkin donuts", "dunkin' donuts"]
  },
  {
    canonicalName: "Taco Bell",
    parentBrandName: "Yum! Brands, Inc.",
    chainStatus: "national",
    verifiedLocationCount: 7800,
    businessModel: "high_frequency_consumer",
    officialWebsite: "tacobell.com",
    aliases: ["taco bell restaurant"]
  },
  {
    canonicalName: "Wendy's",
    parentBrandName: "The Wendy's Company",
    chainStatus: "national",
    verifiedLocationCount: 7000,
    businessModel: "high_frequency_consumer",
    officialWebsite: "wendys.com",
    aliases: ["wendys"]
  },
  {
    canonicalName: "Burger King",
    parentBrandName: "Restaurant Brands International",
    chainStatus: "international",
    verifiedLocationCount: 19000,
    businessModel: "high_frequency_consumer",
    officialWebsite: "bk.com",
    aliases: ["bk", "burger king restaurant"]
  },
  {
    canonicalName: "Chipotle Mexican Grill",
    parentBrandName: "Chipotle Mexican Grill, Inc.",
    chainStatus: "national",
    verifiedLocationCount: 3200,
    businessModel: "high_frequency_consumer",
    officialWebsite: "chipotle.com",
    aliases: ["chipotle"]
  },
  {
    canonicalName: "Panera Bread",
    parentBrandName: "Panera Brands",
    chainStatus: "national",
    verifiedLocationCount: 2100,
    businessModel: "high_frequency_consumer",
    officialWebsite: "panerabread.com",
    aliases: ["panera"]
  },
  {
    canonicalName: "Trader Joe's",
    parentBrandName: "Trader Joe's Co.",
    chainStatus: "national",
    verifiedLocationCount: 560,
    businessModel: "high_frequency_consumer",
    officialWebsite: "traderjoes.com",
    aliases: ["trader joes"]
  },
  {
    canonicalName: "Whole Foods Market",
    parentBrandName: "Amazon.com, Inc.",
    chainStatus: "national",
    verifiedLocationCount: 500,
    businessModel: "high_frequency_consumer",
    officialWebsite: "wholefoodsmarket.com",
    aliases: ["whole foods"]
  },
  {
    canonicalName: "Aldi",
    parentBrandName: "Aldi Einkauf GmbH & Co. oHG",
    chainStatus: "international",
    verifiedLocationCount: 12000,
    businessModel: "high_frequency_consumer",
    officialWebsite: "aldi.us",
    aliases: ["aldi supercenter"]
  },
  {
    canonicalName: "CVS Pharmacy",
    parentBrandName: "CVS Health Corporation",
    chainStatus: "national",
    verifiedLocationCount: 9900,
    businessModel: "high_frequency_consumer",
    officialWebsite: "cvs.com",
    aliases: ["cvs"]
  },
  {
    canonicalName: "Walgreens",
    parentBrandName: "Walgreens Boots Alliance",
    chainStatus: "national",
    verifiedLocationCount: 8800,
    businessModel: "high_frequency_consumer",
    officialWebsite: "walgreens.com",
    aliases: ["walgreens pharmacy"]
  },
  {
    canonicalName: "Best Buy",
    parentBrandName: "Best Buy Co., Inc.",
    chainStatus: "national",
    verifiedLocationCount: 1000,
    businessModel: "high_frequency_consumer",
    officialWebsite: "bestbuy.com",
    aliases: ["bestbuy"]
  },
  {
    canonicalName: "Kroger",
    parentBrandName: "The Kroger Co.",
    chainStatus: "national",
    verifiedLocationCount: 2700,
    businessModel: "high_frequency_consumer",
    officialWebsite: "kroger.com",
    aliases: ["kroger supermarket"]
  },
  {
    canonicalName: "Tenn Fitness",
    parentBrandName: "Tenn Fitness LLC",
    chainStatus: "regional",
    verifiedLocationCount: 15,
    businessModel: "membership_consumer",
    officialWebsite: "tennfitness.com",
    aliases: ["28:tenn fitness", "tenn fitness gym", "28 tenn fitness"]
  }
];

// Explorer Popularity Level classifier
export function getExplorerPopularityLevelFromScore(
  score: number | null,
  confidence: number
): ExplorerPopularityLevel {
  if (score === null || confidence < 0.45) {
    return "limited_popularity";
  }

  if (score >= 80) return "extremely_popular";
  if (score >= 60) return "highly_popular";
  if (score >= 40) return "moderately_popular";
  if (score >= 20) return "limited_popularity";
  return "minimal_popularity";
}

export function getExplorerPopularityLevel(
  score: number | null,
  confidence: number
): ExplorerPopularityLevel {
  return getExplorerPopularityLevelFromScore(score, confidence);
}

export function determinePopularityLevel(
  scoreOrIntel: number | null | Partial<BusinessBusiness> | PopularityIntelligence,
  confidenceParam?: number
): ExplorerPopularityLevel {
  if (typeof scoreOrIntel === "object" && scoreOrIntel !== null) {
    if ("explorerLevel" in scoreOrIntel && scoreOrIntel.explorerLevel) return (scoreOrIntel as PopularityIntelligence).explorerLevel;
    const score = (scoreOrIntel as any).finalScore ?? (scoreOrIntel as any).finalPopularityScore ?? (scoreOrIntel as any).popularity_score ?? null;
    const conf = (scoreOrIntel as any).confidence ?? (scoreOrIntel as any).popularity_confidence ?? 0;
    return getExplorerPopularityLevel(score, conf);
  }
  return getExplorerPopularityLevel(scoreOrIntel as number | null, confidenceParam ?? 0.8);
}

// 4. Verified Location Network Score
export function scoreLocationNetwork(count: number | null): number | null {
  if (count === null) return null;
  if (count >= 5000) return 100;
  if (count >= 2000) return 95;
  if (count >= 1000) return 90;
  if (count >= 500) return 82;
  if (count >= 250) return 72;
  if (count >= 100) return 62;
  if (count >= 25) return 48;
  if (count >= 10) return 35;
  if (count >= 2) return 22;

  return 10;
}

// 5. Business Expansion Speed Score
export function scoreExpansionVelocity(
  annualGrowthRate: number | null
): number | null {
  if (annualGrowthRate === null) return null;
  if (annualGrowthRate >= 0.30) return 100;
  if (annualGrowthRate >= 0.20) return 90;
  if (annualGrowthRate >= 0.12) return 78;
  if (annualGrowthRate >= 0.07) return 65;
  if (annualGrowthRate >= 0.03) return 52;
  if (annualGrowthRate > 0) return 40;

  return 25;
}

// 6. Verified Brand Floor Application
export function applyVerifiedBrandFloor(
  score: number,
  identity: BrandIdentity,
  verifiedLocationCount: number | null
): number {
  if (
    identity.identityConfidence < 0.85 ||
    verifiedLocationCount === null
  ) {
    return score;
  }

  const chainStatusStr = (identity.chainStatus || "").toLowerCase();

  if (
    (chainStatusStr === "international" || chainStatusStr === "international_chain") &&
    verifiedLocationCount >= 1000
  ) {
    return Math.max(score, 85);
  }

  if (
    (chainStatusStr === "national" || chainStatusStr === "national_chain") &&
    verifiedLocationCount >= 500
  ) {
    return Math.max(score, 80);
  }

  if (
    (chainStatusStr === "regional" || chainStatusStr === "regional_chain") &&
    verifiedLocationCount >= 100
  ) {
    return Math.max(score, 65);
  }

  return score;
}

// Growth Signal Decay Helper
export function getGrowthDecay(ageDays: number): number {
  if (ageDays <= 30) return 1.0;
  if (ageDays <= 90) return 0.9;
  if (ageDays <= 180) return 0.7;
  if (ageDays <= 365) return 0.5;
  if (ageDays <= 730) return 0.2;
  return 0.1;
}

// Clean Suspicious Business Names & Match Brand Registry
export function resolveBusinessIdentity(business: Partial<BusinessBusiness>): BrandIdentity & { displayName: string } {
  const rawName = business.name || "";
  let cleanName = rawName.trim();

  // Normalize suspicious prefix patterns like "28:Tenn Fitness" -> "Tenn Fitness"
  if (/^\d{1,3}:/.test(cleanName)) {
    cleanName = cleanName.replace(/^\d{1,3}:/, "").trim();
  }

  const lowerRaw = rawName.toLowerCase();
  const lowerClean = cleanName.toLowerCase();

  for (const entry of BRAND_REGISTRY) {
    const canonicalLower = entry.canonicalName.toLowerCase();
    const matchesCanonical = lowerClean === canonicalLower || lowerClean.startsWith(canonicalLower + " ") || lowerClean.includes(canonicalLower);
    const matchesAlias = entry.aliases.some(a => lowerClean === a || lowerClean.startsWith(a + " ") || lowerRaw.includes(a));

    if (matchesCanonical || matchesAlias) {
      // Ensure exact brand matching prevents false positive associations (e.g. "Starbucks Auto Repair")
      const isCarOrRepair = lowerClean.includes("auto repair") || lowerClean.includes("towing") || lowerClean.includes("legal") || lowerClean.includes("law firm") || lowerClean.includes("plumbing");
      const brandIsRestaurantOrCoffee = entry.canonicalName === "Starbucks" || entry.canonicalName === "McDonald's" || entry.canonicalName === "Chick-fil-A" || entry.canonicalName === "Taco Bell" || entry.canonicalName === "Subway";

      if (isCarOrRepair && brandIsRestaurantOrCoffee) {
        // Disambiguate false positive
        continue;
      }

      const chainStatus = entry.chainStatus.replace("_chain", "") as any;

      return {
        canonicalName: entry.canonicalName,
        parentBrand: entry.parentBrandName,
        parentBrandName: entry.parentBrandName,
        officialDomain: entry.officialWebsite,
        chainStatus,
        identityConfidence: 0.95,
        confidence: 0.95,
        displayName: entry.canonicalName
      };
    }
  }

  // Independent / unmatched business
  return {
    canonicalName: cleanName,
    parentBrand: null,
    parentBrandName: null,
    officialDomain: business.website || null,
    chainStatus: "independent",
    identityConfidence: 0.70,
    confidence: 0.70,
    displayName: cleanName
  };
}

// Weights for Popularity Intelligence V4
export const POPULARITY_V4_WEIGHTS = {
  brandRecognition: 0.20,
  verifiedLocationNetwork: 0.18,
  exactLocationConsumerTraction: 0.17,
  expansionVelocity: 0.12,
  jobCompetition: 0.12,
  surroundingPopularLocations: 0.08,
  localMarketProminence: 0.07,
  consumerEngagement: 0.06,
};

// Weights for Job Competition Sub-score
export const JOB_COMPETITION_WEIGHTS = {
  publicApplicantEvidence: 0.25,
  employerBrandRecognition: 0.20,
  locationNetworkSize: 0.15,
  activeAndRecurringListingVolume: 0.12,
  entryLevelAndStudentFamiliarity: 0.10,
  applicationAccessibility: 0.08,
  localPopulationExposure: 0.05,
  localConsumerPopularity: 0.05,
};

/**
 * Core Popularity Intelligence V4 Calculator
 */
export function calculateBusinessPopularity(
  business: Partial<BusinessBusiness>
): PopularityIntelligence {
  const meta = (business as any).metadata || {};

  // 1. Resolve Brand Identity & Verified Network
  const identity = resolveBusinessIdentity(business);

  const matchedBrand = BRAND_REGISTRY.find(
    b => b.canonicalName === identity.canonicalName
  );

  const verifiedLocationCount = matchedBrand ? matchedBrand.verifiedLocationCount : null;
  const annualGrowthRate = meta.annualLocationGrowthRate ?? meta.growthRate ?? null;

  // 2. Component Scores
  // Exact Location Consumer Traction
  const reviewCount = (business as any).reviewCount ?? business.reviewsCount ?? (business as any).user_ratings_total ?? (business as any).reviews_count ?? null;
  const rating = business.rating ?? null;
  const reviewVelocity = meta.reviewsLast30Days ?? meta.reviewVelocity ?? null;

  let exactLocationTractionScore: number | null = null;
  if (reviewCount !== null && reviewCount !== undefined) {
    if (reviewCount >= 500) {
      exactLocationTractionScore = 90;
    } else if (reviewCount >= 200) {
      exactLocationTractionScore = 80;
    } else if (reviewCount >= 50) {
      exactLocationTractionScore = 65;
    } else if (reviewCount >= 10) {
      exactLocationTractionScore = 45;
    } else if (reviewCount >= 1) {
      exactLocationTractionScore = 25;
    } else {
      exactLocationTractionScore = 15;
    }

    if (reviewVelocity && reviewVelocity >= 15) {
      exactLocationTractionScore = Math.min(100, exactLocationTractionScore + 10);
    }

    if (rating && rating >= 4.7 && reviewCount >= 20) {
      exactLocationTractionScore = Math.min(100, exactLocationTractionScore + 5);
    }
  }

  // Brand Recognition Score
  let brandRecognitionScore: number | null = null;
  if (identity.chainStatus === "international" || (identity.chainStatus as string) === "international_chain") {
    brandRecognitionScore = 95;
  } else if (identity.chainStatus === "national" || (identity.chainStatus as string) === "national_chain") {
    brandRecognitionScore = 82;
  } else if (identity.chainStatus === "regional" || (identity.chainStatus as string) === "regional_chain") {
    brandRecognitionScore = 65;
  } else if (identity.chainStatus === "local_chain") {
    brandRecognitionScore = 45;
  } else if (identity.chainStatus === "independent") {
    if (exactLocationTractionScore !== null && exactLocationTractionScore >= 60) {
      brandRecognitionScore = Math.round(exactLocationTractionScore * 0.85);
    } else {
      brandRecognitionScore = 15;
    }
  }

  // Location Network Score
  const locationNetworkScore = scoreLocationNetwork(verifiedLocationCount);

  // Expansion Velocity Score
  const expansionVelocityScore = scoreExpansionVelocity(annualGrowthRate);

  // Surrounding Area Score
  const nearbyAnchors: NearbyPopularAnchor[] = meta.nearbyAnchors || (business as any).nearbyAnchors || (business as any).locationContext?.nearbyAnchorBusinesses || [];
  const confirmedAnchors = nearbyAnchors.filter(a => (a.confirmedPopularityScore ?? 0) >= 80 || (a as any).isConfirmedPopular);
  const surroundingAreaScore: number | null = confirmedAnchors.length > 0 ? Math.min(100, confirmedAnchors.length * 20) : null;

  // Market Context
  const marketContext: MarketContext = meta.marketContext || (business.address?.toLowerCase().includes("mall") || business.address?.toLowerCase().includes("terminal") ? "dense_urban" : "suburban");
  const marketContextScore: number | null = 60;

  // Consumer Engagement
  const consumerEngagementScore: number | null = business.website ? 75 : 40;

  // Job Competition Score
  let jobCompetitionScore: number | null = null;
  if (brandRecognitionScore !== null || locationNetworkScore !== null || exactLocationTractionScore !== null) {
    const brandPart = (brandRecognitionScore ?? 30) * JOB_COMPETITION_WEIGHTS.employerBrandRecognition;
    const netPart = (locationNetworkScore ?? 20) * JOB_COMPETITION_WEIGHTS.locationNetworkSize;
    const tracPart = (exactLocationTractionScore ?? 30) * JOB_COMPETITION_WEIGHTS.localConsumerPopularity;
    const accPart = 70 * JOB_COMPETITION_WEIGHTS.applicationAccessibility;
    const famPart = (brandRecognitionScore ?? 30) * JOB_COMPETITION_WEIGHTS.entryLevelAndStudentFamiliarity;

    jobCompetitionScore = Math.round((brandPart + netPart + tracPart + accPart + famPart) / (0.20 + 0.15 + 0.05 + 0.08 + 0.10));
  }

  // Check for Unknown / Data Insufficient case:
  const isFailedScraper = meta.scraperStatus === "failed";
  const isDataInsufficient = isFailedScraper || (
    identity.chainStatus === "independent" &&
    reviewCount === null &&
    verifiedLocationCount === null
  );

  if (isDataInsufficient) {
    const emptyIntel: PopularityIntelligence = {
      finalScore: null,
      confidence: 0.30,
      explorerLevel: "limited_popularity",
      brandRecognitionScore: identity.chainStatus === "independent" ? 15 : null,
      locationNetworkScore: null,
      expansionVelocityScore: null,
      exactLocationTractionScore: null,
      surroundingAreaScore: null,
      marketContextScore: null,
      consumerEngagementScore: null,
      jobCompetitionScore: null,
      verifiedLocationCount: null,
      annualLocationGrowthRate: null,
      evidence: [
        { type: "source_collection", detail: "Insufficient local evidence collected for analysis", confidence: "Low" }
      ],
      lastUpdatedAt: new Date().toISOString(),
      algorithmVersion: "popularity_v4",
      businessIdentity: identity,
      marketContext: "unknown",
      nearbyAnchors: nearbyAnchors,
      jobCompetitionEvidence: [],
      rawScore: null,
      appliedFloors: [],
      appliedCaps: [],
      summaryText: "Insufficient data available to verify popularity.",
      calculatedAt: new Date().toISOString(),
      sourceUpdatedAt: null,
      missingSources: ["review_data", "location_directory"],
      failedLookups: isFailedScraper ? ["scraper"] : [],

      // Backward compatibility
      brandPopularityScore: identity.chainStatus === "independent" ? 15 : null,
      locationPopularityScore: null,
      marketPresenceScore: null,
      growthMomentumScore: null,
      locationContextScore: null,
      finalPopularityScore: null,
      level: "unknown",
      popularity_score: 0,
      popularity_level: "Popularity Unknown",
      popularity_confidence: 0.30,
      popularity_evidence: "Insufficient data available",
      popularity_evidence_items: [],
      popularity_last_checked_at: new Date().toISOString(),
      popularity_source_updated_at: null,
      signal: "unknown",
      reasons: ["Data insufficient for popularity analysis"],
      score: 0,
      canonicalLevel: "unknown",
      localPercentile: null
    };
    return emptyIntel;
  }

  // 3. Weighted Score Calculation
  const componentWeights: [number | null, number][] = [
    [brandRecognitionScore, POPULARITY_V4_WEIGHTS.brandRecognition],
    [locationNetworkScore, POPULARITY_V4_WEIGHTS.verifiedLocationNetwork],
    [exactLocationTractionScore, POPULARITY_V4_WEIGHTS.exactLocationConsumerTraction],
    [expansionVelocityScore, POPULARITY_V4_WEIGHTS.expansionVelocity],
    [jobCompetitionScore, POPULARITY_V4_WEIGHTS.jobCompetition],
    [surroundingAreaScore, POPULARITY_V4_WEIGHTS.surroundingPopularLocations],
    [marketContextScore, POPULARITY_V4_WEIGHTS.localMarketProminence],
    [consumerEngagementScore, POPULARITY_V4_WEIGHTS.consumerEngagement],
  ];

  let weightedSum = 0;
  let availableWeight = 0;

  for (const [val, w] of componentWeights) {
    if (val !== null && val !== undefined) {
      weightedSum += val * w;
      availableWeight += w;
    }
  }

  let rawCalculatedScore = availableWeight > 0 ? Math.round(weightedSum / availableWeight) : 50;

  // 4. Apply Brand Floor & Capping Rules
  const appliedFloors: string[] = [];
  const appliedCaps: string[] = [];

  const flooredScore = applyVerifiedBrandFloor(
    rawCalculatedScore,
    identity,
    verifiedLocationCount
  );

  if (flooredScore > rawCalculatedScore) {
    appliedFloors.push(`Verified Brand Floor (${flooredScore})`);
  }

  let finalScore = flooredScore;

  // Cap: Brand recognition alone cannot produce extreme branch score without exact location traction (capped at 79)
  if (
    exactLocationTractionScore === null &&
    identity.chainStatus !== "independent" &&
    finalScore >= 80
  ) {
    finalScore = 79;
    appliedCaps.push("Missing exact location traction cap (79)");
  }

  // 5. Calculate Confidence
  let confidence = 0.70;
  if (identity.identityConfidence >= 0.85) confidence += 0.15;
  if (exactLocationTractionScore !== null) confidence += 0.10;
  confidence = Math.min(0.98, confidence);

  const explorerLevel = getExplorerPopularityLevel(finalScore, confidence);

  // Evidence list
  const evidence: PopularityEvidence[] = [
    { type: "brand_identity", detail: `Identified as ${identity.canonicalName} (${identity.chainStatus})`, confidence: "High" }
  ];
  if (verifiedLocationCount !== null) {
    evidence.push({ type: "location_network", detail: `Verified ${verifiedLocationCount.toLocaleString()} locations nationally`, confidence: "High" });
  }
  if (exactLocationTractionScore !== null) {
    evidence.push({ type: "consumer_traction", detail: `Consumer traction score ${exactLocationTractionScore}/100 from review activity`, confidence: "High" });
  }

  const legacyLevelStr = explorerLevel === "extremely_popular" ? "extremely_popular"
    : explorerLevel === "highly_popular" ? "high"
    : explorerLevel === "moderately_popular" ? "popular"
    : explorerLevel === "limited_popularity" ? "low"
    : "very_low";

  const labelStr = EXPLORER_POPULARITY_LABELS[explorerLevel];

  return {
    finalScore,
    confidence,
    explorerLevel,

    brandRecognitionScore,
    locationNetworkScore,
    expansionVelocityScore,
    exactLocationTractionScore,
    surroundingAreaScore,
    marketContextScore,
    consumerEngagementScore,
    jobCompetitionScore,

    verifiedLocationCount,
    annualLocationGrowthRate: annualGrowthRate,

    evidence,
    lastUpdatedAt: new Date().toISOString(),
    algorithmVersion: "popularity_v4",

    businessIdentity: identity,
    marketContext,
    nearbyAnchors,
    jobCompetitionEvidence: [],
    rawScore: rawCalculatedScore,
    appliedFloors,
    appliedCaps,
    summaryText: `${identity.canonicalName} is classified as ${labelStr} based on verified brand reach, location network size, and consumer traction signals.`,
    calculatedAt: new Date().toISOString(),
    sourceUpdatedAt: new Date().toISOString(),
    missingSources: [],
    failedLookups: [],

    // Backward compatibility fields
    brandPopularityScore: brandRecognitionScore,
    locationPopularityScore: exactLocationTractionScore,
    marketPresenceScore: locationNetworkScore,
    growthMomentumScore: expansionVelocityScore,
    locationContextScore: surroundingAreaScore,
    finalPopularityScore: finalScore,
    localProminenceScore: marketContextScore ?? 60,
    freshnessMultiplier: 1.0,
    reviewVelocityScore: meta.reviewsLast30Days ? Math.min(100, meta.reviewsLast30Days * 4) : 50,
    displayedLastUpdatedAt: new Date().toISOString(),
    level: legacyLevelStr as any,
    popularity_score: finalScore ?? 0,
    popularity_level: labelStr,
    popularity_confidence: confidence,
    popularity_evidence: `${identity.canonicalName} popularity intelligence assessment.`,
    popularity_evidence_items: evidence as any,
    popularity_last_checked_at: new Date().toISOString(),
    popularity_source_updated_at: new Date().toISOString(),
    signal: finalScore && finalScore >= 80 ? "high" : finalScore && finalScore >= 60 ? "probable" : "improbable",
    reasons: [`Calculated using Popularity Intelligence V4`],
    score: finalScore ?? 0,
    canonicalLevel: legacyLevelStr as any,
    localPercentile: finalScore ? Math.min(99, Math.max(1, finalScore)) : null
  };
}

/**
 * Backward compatibility aliases and signal definitions
 */
export const BUSINESS_EXPLORER_POPULARITY_RANK: Record<string, number> = {
  extremely_popular: 5,
  highly_popular: 4,
  moderately_popular: 3,
  limited_popularity: 2,
  minimal_popularity: 1,
  unknown: 0
};

export const popularitySignalLabels: Record<string, string> = {
  extremely_popular: "Extremely Popular",
  highly_popular: "Highly Popular",
  moderately_popular: "Moderately Popular",
  limited_popularity: "Limited Popularity",
  minimal_popularity: "Minimal Popularity",
  unknown: "Limited Popularity"
};

export function getBusinessExplorerPopularitySignal(popularityInput?: any): string {
  return getBusinessExplorerPrimaryPopularityLabel(popularityInput);
}

export function getBusinessExplorerPopularityAdjustment(popularityInput: any): number {
  return getPopularityAdjustment(popularityInput, "business_explorer");
}

/**
 * Primary Popularity Label Generator for Business Explorer
 */
export function getBusinessExplorerPrimaryPopularityLabel(
  popularityInput?: number | string | Partial<BusinessBusiness> | PopularityIntelligence | null
): string {
  if (typeof popularityInput === "object" && popularityInput !== null) {
    if ("explorerLevel" in popularityInput && popularityInput.explorerLevel) {
      return EXPLORER_POPULARITY_LABELS[popularityInput.explorerLevel] || "Moderately Popular";
    }
    const score = (popularityInput as any).finalScore ?? (popularityInput as any).finalPopularityScore ?? (popularityInput as any).popularity_score ?? null;
    const conf = (popularityInput as any).confidence ?? (popularityInput as any).popularity_confidence ?? 0;
    const level = getExplorerPopularityLevelFromScore(score, conf);
    return EXPLORER_POPULARITY_LABELS[level];
  }

  if (typeof popularityInput === "number") {
    const level = getExplorerPopularityLevelFromScore(popularityInput, 0.8);
    return EXPLORER_POPULARITY_LABELS[level];
  }

  if (typeof popularityInput === "string") {
    const str = popularityInput.toLowerCase();
    if (str.includes("extremely_popular") || str.includes("extremely popular") || str.includes("extreme")) return "Extremely Popular";
    if (str.includes("minimal_popularity") || str.includes("minimal popular") || str.includes("minimal") || str.includes("extremely_unpopular")) return "Minimal Popularity";
    if (str.includes("highly_popular") || str.includes("highly popular") || str.includes("highly")) return "Highly Popular";
    if (str.includes("limited_popularity") || str.includes("limited popular") || str.includes("limited") || str.includes("unpopular")) return "Limited Popularity";
    if (str.includes("moderately_popular") || str.includes("moderately popular") || str.includes("moderate")) return "Moderately Popular";
    if (str.includes("popular")) return "Highly Popular";
  }

  return "Moderately Popular";
}

/**
 * Explanatory Subtitle Generator for Popularity Levels
 */
export function getPopularitySubtitle(
  level: ExplorerPopularityLevel | string
): string {
  const l = typeof level === "string" ? level.toLowerCase() : level;
  if (l.includes("extremely_popular") || l.includes("extremely popular") || l === "extremely_popular") {
    return "High public recognition";
  }
  if (l.includes("highly_popular") || l.includes("highly popular") || l === "highly_popular") {
    return "Strong local and brand visibility";
  }
  if (l.includes("moderately_popular") || l.includes("moderately popular") || l === "moderately_popular" || l.includes("moderate")) {
    return "Moderate local recognition";
  }
  if (l.includes("minimal_popularity") || l.includes("minimal popular") || l === "minimal_popularity" || l.includes("minimal") || l.includes("extremely_unpopular")) {
    return "Very limited public traction";
  }
  if (l.includes("limited_popularity") || l.includes("limited popular") || l === "limited_popularity" || l.includes("limited") || l.includes("unpopular")) {
    return "Limited public traction";
  }
  return "Moderate local recognition";
}

/**
 * Secondary Match Sub-label for Business Explorer Card
 */
export function getBusinessExplorerMatchLabel(
  popularityInput?: any
): string {
  const level = determinePopularityLevel(popularityInput);
  return getPopularitySubtitle(level);
}

/**
 * Tier Rank Mapping for Sorting
 */
export function getPopularityTierRank(level?: string | null): number {
  if (!level) return 0;
  const l = level.toLowerCase();
  if (l === "extremely_popular" || l === "extremely popular" || l === "extremely_high") return 5;
  if (l === "highly_popular" || l === "highly popular" || l === "very_popular" || l === "high") return 4;
  if (l === "moderately_popular" || l === "moderately popular" || l === "moderate" || l === "popular") return 3;
  if (l === "limited_popularity" || l === "limited popularity" || l === "unpopular" || l === "limited" || l === "low") return 2;
  if (l === "minimal_popularity" || l === "minimal popularity" || l === "extremely_unpopular" || l === "minimal" || l === "very_low") return 1;
  return 0;
}

/**
 * Numerical Score Extraction for Tier Tie-Breaking
 */
export function getEffectivePopularityScore(popularityInput?: any): number | null {
  if (popularityInput === null || popularityInput === undefined) return null;
  if (typeof popularityInput === "number") return popularityInput;
  return popularityInput.finalScore ?? popularityInput.finalPopularityScore ?? popularityInput.popularity_score ?? null;
}

/**
 * Popularity Adjustment for Ranking Scores
 */
export function getPopularityAdjustment(
  popularityInput: number | string | Partial<BusinessBusiness> | PopularityIntelligence | any,
  audienceMode: string
): number {
  let score: number | null = 50;
  let confidence = 0.8;
  let level: ExplorerPopularityLevel = "moderately_popular";
  let jobCompScore: number | null = null;

  if (typeof popularityInput === "object" && popularityInput !== null) {
    if ("explorerLevel" in popularityInput || "finalScore" in popularityInput) {
      score = popularityInput.finalScore ?? popularityInput.finalPopularityScore ?? popularityInput.popularity_score ?? null;
      confidence = popularityInput.confidence ?? 0.8;
      level = popularityInput.explorerLevel || getExplorerPopularityLevel(score, confidence);
      jobCompScore = popularityInput.jobCompetitionScore ?? null;
    } else {
      const intel = calculateBusinessPopularity(popularityInput);
      score = intel.finalScore;
      confidence = intel.confidence;
      level = intel.explorerLevel;
      jobCompScore = intel.jobCompetitionScore;
    }
  } else if (typeof popularityInput === "number") {
    score = popularityInput;
    level = getExplorerPopularityLevel(score, 0.8);
  }

  if (score === null || confidence < 0.45) {
    return 0;
  }

  if (audienceMode === "business_explorer") {
    if (level === "extremely_popular") return 25;
    if (level === "highly_popular") return 20;
    if (level === "moderately_popular") return 15;
    if (level === "limited_popularity") return 5;
    if (level === "minimal_popularity") return 0;
    return 0;
  }

  // Non-Business Explorer modes:
  // Penalty applies ONLY when explorerLevel === "extremely_popular"
  if (level !== "extremely_popular") {
    return 0;
  }

  if (jobCompScore !== null && jobCompScore >= 85) {
    return -9;
  }
  return -5;
}

/**
 * Audience Warning Generator
 */
export function getAudienceWarning(
  audienceMode: string,
  popularity: PopularityIntelligence | any
): string | null {
  if (
    audienceMode === "business_explorer" ||
    !popularity ||
    (popularity.explorerLevel !== "extremely_popular" && popularity.level !== "extremely_popular")
  ) {
    return null;
  }

  switch (audienceMode) {
    case "job_seeker":
      return "High Competition";
    case "recruiter":
      return "High Recruiter Competition";
    case "staffing_agency":
      return "High Agency Competition";
    case "career_coach":
      return "Highly Competitive Employer";
    case "workforce_organization":
      return "High Applicant Demand";
    case "college_career_center":
      return "High Student Competition";
    case "sales_professional":
      return "High Sales Competition";
    case "business_development":
      return "High Partnership Competition";
    case "agency_freelancer":
      return "High Client Competition";
    default:
      return null;
  }
}

export function getAudiencePopularityWarningReason(
  audienceMode: string,
  popularity?: any
): string {
  const name = popularity?.businessIdentity?.canonicalName || "This business";
  switch (audienceMode) {
    case "job_seeker":
      return `${name} is a highly visible destination with significant job-applicant competition.`;
    case "recruiter":
      return `${name} has direct recruiting reach, increasing recruiter outreach competition.`;
    case "staffing_agency":
      return `${name} maintains structured vendor pools and direct applicant volume.`;
    case "career_coach":
      return `${name} is a highly targeted employer; job seekers must stand out.`;
    case "workforce_organization":
      return `${name} attracts strong public job seeker demand.`;
    case "college_career_center":
      return `${name} is widely recognized among students, resulting in high application volume.`;
    case "sales_professional":
      return `${name} receives frequent sales and vendor inquiries.`;
    case "business_development":
      return `${name} has strong regional or national brand visibility.`;
    case "agency_freelancer":
      return `${name} receives high outreach volume from agencies and freelancers.`;
    default:
      return `${name} has high market visibility and competition.`;
  }
}

export function getPopularityWarningReason(audienceMode: string, popularity?: any): string {
  return getAudiencePopularityWarningReason(audienceMode, popularity);
}

export function shouldShowPopularityWarning(
  popularityScore: number,
  popularityConfidence: number,
  audienceMode: string,
  level?: string
): boolean {
  if (audienceMode === "business_explorer") {
    return false;
  }
  if (level) {
    return level === "extremely_popular";
  }
  return popularityScore >= 80 && popularityConfidence >= 0.72;
}

export function createAudiencePopularityWarning(
  audienceMode: string,
  popularityResult?: any
): AudienceWarning | null {
  if (audienceMode === "business_explorer" || !popularityResult) {
    return null;
  }

  const label = getAudienceWarning(audienceMode, popularityResult);
  if (!label) {
    return null;
  }

  return {
    key: `popularity_warning_${audienceMode}`,
    label,
    reason: getAudiencePopularityWarningReason(audienceMode, popularityResult),
    severity: "subtle"
  };
}

export function createPopularityWarning(
  audienceMode: string,
  popularityResult?: any
): AudienceWarning | null {
  return createAudiencePopularityWarning(audienceMode, popularityResult);
}

export function getPopularityWarning(
  popularityInput?: Partial<BusinessBusiness> | PopularityLevel | string | number,
  audienceModeOrConfidence?: string | number,
  audienceModeParam?: string
): string | null {
  let audienceMode = "job_seeker";
  let intel: any = null;

  if (typeof popularityInput === "object" && popularityInput !== null) {
    intel = calculateBusinessPopularity(popularityInput);
    if (typeof audienceModeOrConfidence === "string") {
      audienceMode = audienceModeOrConfidence;
    }
  } else if (typeof popularityInput === "number") {
    if (typeof audienceModeOrConfidence === "string") {
      audienceMode = audienceModeOrConfidence;
    } else if (audienceModeParam) {
      audienceMode = audienceModeParam;
    }
    intel = { finalScore: popularityInput, confidence: 0.8, explorerLevel: getExplorerPopularityLevel(popularityInput, 0.8) };
  } else if (typeof popularityInput === "string") {
    if (typeof audienceModeOrConfidence === "string") {
      audienceMode = audienceModeOrConfidence;
    }
    const str = popularityInput.toLowerCase();
    const isExtreme = str.includes("extreme") && !str.includes("unpopular");
    intel = { finalScore: isExtreme ? 90 : 50, confidence: 0.8, explorerLevel: isExtreme ? "extremely_popular" : "moderately_popular" };
  }

  return getAudienceWarning(audienceMode, intel);
}

export function getTopTagsForAudience(
  business: Partial<BusinessBusiness>,
  distanceInMiles: number | null,
  audienceMode: string,
  rawTags: string[] = []
): string[] {
  if (audienceMode === "business_explorer") {
    const candidates: string[] = [];

    if (business.hours && business.hours.toLowerCase().includes("open")) {
      candidates.push("Open now");
    }

    if (business.rating && business.rating >= 4.6) {
      candidates.push("Highly rated");
    } else if (business.rating && business.rating >= 4.0 && ((business as any).reviewsCount || (business as any).reviewCount || 0) >= 15) {
      candidates.push("Strong reviews");
    }

    const popIntel = calculateBusinessPopularity(business);
    if (popIntel.explorerLevel === "extremely_popular") {
      candidates.push("Top Local Spot");
    } else if (popIntel.explorerLevel === "moderately_popular") {
      candidates.push("Well Established");
    }

    if (distanceInMiles !== null && distanceInMiles !== undefined && distanceInMiles <= 3.0) {
      candidates.push("Nearby");
    }

    if (business.website) {
      candidates.push("Website available");
    }

    const result: string[] = [];
    for (const tag of candidates) {
      if (result.length >= 3) break;
      if (!result.includes(tag)) result.push(tag);
    }
    return result;
  } else {
    const forbiddenExplorerTags = new Set([
      "Top Local Spot",
      "Highly recognized",
      "Well established",
      "Local favorite",
      "Many locations",
      "Hidden Gem"
    ]);

    const filtered = rawTags.filter(t => !forbiddenExplorerTags.has(t));
    const result: string[] = [];
    for (const tag of filtered) {
      if (result.length >= 3) break;
      if (!result.includes(tag)) result.push(tag);
    }

    if (result.length < 2 && distanceInMiles !== null && distanceInMiles <= 5) {
      if (!result.includes("Short Commute")) result.push("Short Commute");
    }
    if (result.length < 3 && business.rating && business.rating >= 4.0) {
      if (!result.includes("Strong Rating")) result.push("Strong Rating");
    }

    return result.slice(0, 3);
  }
}
